/* eslint-disable @intercom/intercom/no-bare-strings */
/* RESPONSIBLE TEAM: team-automation-foundations */

import { type AttributeGroupList } from 'embercom/objects/visual-builder/configuration/editor';

export interface AttributeGroupBuilderOptions {
  attributeService: any;
  appService: any;
  additionalAttributes?: any[];
  includeTicketAttributeGroups?: boolean;
}

/**
 * Builds attribute groups for workflow audience targeting.
 * This replicates the exact logic from the old audience-rules-editor component
 * to ensure feature parity.
 */
export class AttributeGroupBuilder {
  private attributeService: any;
  private appService: any;
  private additionalAttributes: any[];
  private includeTicketAttributeGroups: boolean;

  constructor({
    attributeService,
    appService,
    additionalAttributes = [],
    includeTicketAttributeGroups = false,
  }: AttributeGroupBuilderOptions) {
    this.attributeService = attributeService;
    this.appService = appService;
    this.additionalAttributes = additionalAttributes;
    this.includeTicketAttributeGroups = includeTicketAttributeGroups;
  }

  getAttributeGroups(): AttributeGroupList {
    // EXACT replication of audience-rules-editor.js:145-194
    // Build base attributes the same way as the old component
    let baseAttributes = this.buildBaseAttributes();

    // Convert to groups with deduplication (uniqBy identifier) - EXACT old component logic
    let uniqueAttributes = this.deduplicateAttributes(baseAttributes);
    let groups = this.attributeService.attributesToGroupList(uniqueAttributes);

    // Only add ticket attributes if config specifies includeTicketAttributeGroups
    // This matches audience-rules-editor.js:174-176
    if (this.includeTicketAttributeGroups) {
      groups = [...groups, ...this.attributeService.ticketAttributesByType];
    }

    return groups;
  }

  /**
   * Build base attributes matching the old audience-rules-editor component logic.
   * This replicates the attributeGroupList getter from audience-rules-editor.js:145-168
   */
  private buildBaseAttributes(): any[] {
    // Start with additionalAttributes from config (matches old component line 146)
    // For button/inbound/outbound: [capacityAttributes, finUsageAttributes]
    // For triggerable/tickets: visualBotBuilderConditionalAttributes
    let attributes = [...this.additionalAttributes];

    // Add user attributes (matches old component line 153)
    attributes = attributes.concat(
      this.attributeService.filterableUserAttributesForMessaging || [],
    );

    // Add company attributes if companies are active (matches old component line 155-159)
    if (this.appService.app?.companiesActive) {
      attributes = attributes.concat(
        this.attributeService.filterableCompanyAttributesForMessaging || [],
      );
    }

    // Add additional attributes for leads and visitors (matches old component line 161-165)
    attributes = attributes.concat(
      this.attributeService.additionalAttributesForLeadsAndVisitors || [],
    );

    return attributes;
  }

  /**
   * Deduplicate attributes by identifier.
   */
  private deduplicateAttributes(attributes: any[]): any[] {
    let seen = new Set();
    return attributes.filter((attr) => {
      if (!attr?.identifier) {
        return false;
      }
      if (seen.has(attr.identifier)) {
        return false;
      }
      seen.add(attr.identifier);
      return true;
    });
  }
}
